import { Button, Drawer, Flex, Form, Input, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { IconPickerValue } from 'src/components'
import { paths } from 'src/router'

import { useWorkspaceAppsList } from '../../hooks/useWorkspaceAppsList'
import { buildHttpUrlRule } from '../../utils/appFormValidators'
import { appIconFromPickerValue } from '../../utils/appIcon'
import { AppIcon } from '../AppIcon'
import { AppIconField } from '../AppIconField'

const { Text, Title } = Typography

type Props = {
  workspaceId: number
  open: boolean
  onClose: () => void
}

const APP_TYPE = 'third_party_custom'

type FormValues = {
  name: string
  description?: string
  appDomain?: string
  appUrl?: string
  icon?: IconPickerValue | null
}

export const CreateCustomAppDrawer = ({
  workspaceId,
  open,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm<FormValues>()

  const { createCustomApp, isCreatingCustomApp } =
    useWorkspaceAppsList(workspaceId)

  const previewName = Form.useWatch('name', form)
  const previewIcon = Form.useWatch('icon', form)

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    const created = await createCustomApp({
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      appDomain: values.appDomain?.trim() || undefined,
      appUrl: values.appUrl?.trim() || undefined,
      icon: values.icon ?? null
    })
    handleClose()
    navigate(paths.workspaces.apps.details(String(workspaceId), created.id))
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      size={520}
      destroyOnHidden
      title={
        <Flex align="center" gap={10}>
          <AppIcon
            type={APP_TYPE}
            size={30}
            {...appIconFromPickerValue(previewIcon)}
          />
          <Flex vertical style={{ lineHeight: 1.2 }}>
            <Title level={5} style={{ margin: 0, fontSize: 16 }}>
              {previewName?.trim() ||
                t('workspace.apps.createCustom.defaultTitle')}
            </Title>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('workspace.apps.createCustom.defaultSubtitle')}
            </Text>
          </Flex>
        </Flex>
      }
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={handleClose}>
            {t('workspace.apps.createCustom.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isCreatingCustomApp}
            onClick={() => form.submit()}
          >
            {t('workspace.apps.createCustom.submit')}
          </Button>
        </Flex>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark="optional"
        onFinish={handleSubmit}
      >
        <Form.Item
          label={t('workspace.apps.createCustom.fields.name')}
          name="name"
          extra={t('workspace.apps.createCustom.fields.nameExtra')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('required_field_error')
            }
          ]}
        >
          <Input
            placeholder={t(
              'workspace.apps.createCustom.fields.namePlaceholder'
            )}
            autoFocus
          />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.createCustom.fields.icon')}
          name="icon"
        >
          <AppIconField appType={APP_TYPE} />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.createCustom.fields.description')}
          name="description"
          extra={t('workspace.apps.createCustom.fields.descriptionExtra')}
        >
          <Input.TextArea
            rows={2}
            maxLength={140}
            showCount
            placeholder={t(
              'workspace.apps.createCustom.fields.descriptionPlaceholder'
            )}
          />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.createCustom.connection.appUrl')}
          name="appUrl"
          extra={t(
            'workspace.apps.createCustom.connection.appUrlExtra',
            'Public launch URL for the app. http:// or https:// are both accepted.'
          )}
          rules={[
            buildHttpUrlRule(
              t(
                'workspace.apps.custom.fields.urlInvalid',
                'Enter a valid URL starting with http:// or https://'
              )
            )
          ]}
        >
          <Input placeholder="https://app.example.com" />
        </Form.Item>

        <Form.Item
          label={t('workspace.apps.createCustom.connection.domain')}
          name="appDomain"
          extra={t('workspace.apps.createCustom.connection.domainExtra')}
        >
          <Input prefix="https://" placeholder="example.com" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
